import { useTranslation } from "react-i18next";
import { Card, Group, Progress, Stack, Text, Tooltip } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";

import { TierInfo } from "@customTypes/earnings";

interface TierProgressCardProps {
  tierInfo: TierInfo | undefined;
  isLoading: boolean;
}

export const TierProgressCard = ({ tierInfo, isLoading }: TierProgressCardProps) => {
  const { t, i18n } = useTranslation();

  if (isLoading || !tierInfo) {
    return <Card withBorder shadow="sm" radius="md" padding="lg"><Text>{t("loading")}...</Text></Card>;
  }

  if (!tierInfo.tier_enabled) {
    return (
      <Card withBorder shadow="sm" radius="md" padding="lg">
        <Group justify="space-between" mb="xs">
          <Text fw={500} size="lg">{t("commission-rate")}</Text>
          <Text fw={700} size="xl" c="blue">{tierInfo.default_rate}%</Text>
        </Group>
        <Text size="sm" c="dimmed">{tierInfo.message || t("tier-system-not-enabled")}</Text>
      </Card>
    );
  }

  return (
    <Card withBorder shadow="sm" radius="md" padding="lg">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={500} size="lg">{t("current-tier")}</Text>
            <Text size="xl" fw={700} c="blue">
              Tier {tierInfo.current_tier?.level} ({tierInfo.current_tier?.rate}%)
            </Text>
          </div>
          <div style={{ textAlign: 'right' }}>
            <Text size="sm" c="dimmed">{t("monthly-earnings")}</Text>
            <Text fw={600}>
              {Intl.NumberFormat(i18n.language, { style: "currency", currency: tierInfo.currency || "CFA" }).format(tierInfo.monthly_earnings || 0)}
            </Text>
          </div>
        </Group>

        {tierInfo.next_tier && (
          <div>
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={500}>
                {t("progress-to-tier")} {tierInfo.next_tier.next_tier_level} ({tierInfo.next_tier.rate}%)
              </Text>
              <Text size="sm" fw={500}>
                {tierInfo.next_tier.progress_percentage}%
              </Text>
            </Group>
            
            <Progress 
              value={tierInfo.next_tier.progress_percentage} 
              size="xl" 
              radius="xl" 
              color="blue" 
              striped 
              animated 
            />
            
            <Group justify="space-between" mt="xs">
              <Text size="xs" c="dimmed">
                {t("earned")}: {Intl.NumberFormat(i18n.language, { style: "currency", currency: tierInfo.currency || "CFA" }).format(tierInfo.monthly_earnings || 0)}
              </Text>
              <Group gap={4}>
                <Text size="xs" c="dimmed">
                  {t("needed")}: {Intl.NumberFormat(i18n.language, { style: "currency", currency: tierInfo.currency || "CFA" }).format(tierInfo.next_tier.remaining_to_next_tier)}
                </Text>
                <Tooltip label={t("earn-more-to-level-up")} withArrow>
                  <IconInfoCircle size={14} color="var(--mantine-color-gray-5)" />
                </Tooltip>
              </Group>
            </Group>
          </div>
        )}

        {!tierInfo.next_tier && (
          <Text size="sm" c="green" fw={500} ta="center" mt="sm">
            {t("highest-tier-reached")} 🎉
          </Text>
        )}
      </Stack>
    </Card>
  );
};
